import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload, Video, ShieldCheck, ShieldAlert, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Recorder } from "../components/Recorder";
import { kidApi, errMsg } from "../lib/api";
import { CATEGORIES } from "../lib/constants";
import { probeVideo, MAX_SECONDS } from "../lib/media";
import { useAuth } from "../context/AuthContext";

const SafetyBadge = ({ check }) => {
  if (!check) return null;
  if (check.loading) return <span data-testid="safety-badge-checking" className="zn-chip !py-1 bg-white text-xs"><Loader2 size={14} className="animate-spin" /> Checking…</span>;
  return check.safe
    ? <span data-testid="safety-badge-safe" className="zn-chip !py-1 bg-[#B8F2C9] text-xs"><ShieldCheck size={14} /> Looks kind & safe</span>
    : <span data-testid="safety-badge-unsafe" className="zn-chip !py-1 bg-[#FF8FC7] text-xs"><ShieldAlert size={14} /> {check.reason || "Try different words"}</span>;
};

export default function Studio() {
  const { kid } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("record");
  const [media, setMedia] = useState(null);
  const [mediaErr, setMediaErr] = useState("");
  const file = media?.file;
  const [params] = useSearchParams();
  const [title, setTitle] = useState(() => (params.get("title") || "").slice(0, 80));
  const [desc, setDesc] = useState("");
  const allowed = CATEGORIES.filter((c) => kid.settings?.allowed_categories?.includes(c.id));
  const [category, setCategory] = useState(allowed[0]?.id || "");
  const [check, setCheck] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    const text = `${title} ${desc}`.trim();
    if (text.length < 3) return setCheck(null);
    setCheck({ loading: true });
    const t = setTimeout(() => {
      kidApi.post("/kid/safety-check", { text: `Title: ${title}\nDescription: ${desc}` }).then((r) => setCheck(r.data)).catch(() => setCheck(null));
    }, 900);
    return () => clearTimeout(t);
  }, [title, desc]);

  if (!kid.settings?.uploads_enabled) {
    return <div data-testid="uploads-disabled" className="zn-card p-10 text-center max-w-xl mx-auto"><h2 className="text-3xl font-bold mb-2">Studio is closed</h2><p>Your grown-up has paused uploads for now.</p></div>;
  }

  const pickFile = async (f) => {
    setMedia(null);
    setMediaErr("");
    if (!f) return;
    if (f.size > 30 * 1024 * 1024) return setMediaErr("That video is too big (max 30MB).");
    try {
      const { duration, frames } = await probeVideo(f);
      if (duration > MAX_SECONDS + 0.5) return setMediaErr(`Zunii videos are ${MAX_SECONDS} seconds max. This one is ${Math.round(duration)}s. Try a shorter clip!`);
      setMedia({ file: f, duration, frames });
    } catch {
      setMedia({ file: f, duration: 0, frames: [] });
    }
  };

  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("description", desc);
    fd.append("category", category);
    fd.append("source", mode);
    if (media.duration) fd.append("duration", String(media.duration));
    media.frames.forEach((b, i) => fd.append("frames", b, `frame${i}.jpg`));
    setProgress(0);
    try {
      await kidApi.post("/kid/videos", fd, { onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)) });
      toast.success("Sent to your grown-up for approval!");
      nav("/kid/mine");
    } catch (e) {
      toast.error(errMsg(e));
      setProgress(null);
    }
  };

  const canSubmit = file && title.trim() && category && progress === null && !check?.loading && check?.safe !== false;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
      <div className="lg:col-span-7">
        <h1 className="text-3xl sm:text-5xl font-bold mb-4 sm:mb-6">Studio</h1>
        <div className="grid grid-cols-2 sm:flex gap-2 mb-4 sm:mb-5">
          <button data-testid="studio-mode-record" onClick={() => { setMode("record"); setMedia(null); setMediaErr(""); }} className={`zn-chip justify-center ${mode === "record" ? "bg-[#FFD23F]" : "bg-white"}`}><Video size={16} /> Record<span className="hidden sm:inline"> a clip</span></button>
          <button data-testid="studio-mode-upload" onClick={() => { setMode("upload"); setMedia(null); setMediaErr(""); }} className={`zn-chip justify-center ${mode === "upload" ? "bg-[#FFD23F]" : "bg-white"}`}><Upload size={16} /> Upload<span className="hidden sm:inline"> a video</span></button>
        </div>
        {mode === "record" ? <Recorder onRecorded={setMedia} /> : (
          <label data-testid="upload-dropzone" className="zn-card zn-lift aspect-[4/3] sm:aspect-video grid place-items-center text-center cursor-pointer bg-[#3DCCC7] zn-dots">
            <input data-testid="upload-file-input" type="file" accept="video/*" className="hidden" onChange={(e) => pickFile(e.target.files[0] || null)} />
            <div className="bg-white rounded-3xl border-[3px] border-[#1B1A2E] p-5 sm:p-6 mx-4">
              <Upload size={32} className="mx-auto mb-2" />
              <p className="font-display text-lg sm:text-xl font-semibold break-all">{file ? file.name : "Pick a video"}</p>
              <p className="text-sm text-[#1B1A2E]/60">{media ? (media.duration ? `${media.duration.toFixed(1)}s · ready!` : "Ready! We'll check the length when you send it.") : `Up to ${MAX_SECONDS} seconds · MP4, WebM or MOV`}</p>
            </div>
          </label>
        )}
        {mediaErr && <p data-testid="studio-media-error" className="mt-3 text-[#E8442A] font-semibold">{mediaErr}</p>}
      </div>

      <div className="lg:col-span-5 zn-card p-5 sm:p-8 space-y-5 h-fit lg:mt-[7.5rem]">
        <div>
          <div className="flex items-center justify-between gap-2 mb-2"><label className="font-display font-semibold text-lg">Title</label><SafetyBadge check={check} /></div>
          <input data-testid="studio-title-input" className="zn-input" placeholder="My amazing paper rocket" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} enterKeyHint="next" autoComplete="off" />
        </div>
        <div>
          <label className="font-display font-semibold text-lg block mb-2">Tell us about it</label>
          <textarea data-testid="studio-description-input" className="zn-input min-h-[80px]" rows={3} placeholder="What did you make?" value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={500} />
        </div>
        <div>
          <label className="font-display font-semibold text-lg block mb-2">Category</label>
          <div className="flex sm:flex-wrap gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 sm:mx-0 sm:px-0 pb-1">
            {allowed.map(({ id, label, icon: Icon, color }) => (
              <button key={id} data-testid={`studio-category-${id}`} onClick={() => setCategory(id)} className="zn-chip shrink-0" style={{ background: category === id ? color : "#fff" }}><Icon size={15} /> {label}</button>
            ))}
          </div>
        </div>
        {progress !== null && (
          <div data-testid="upload-progress" className="h-4 rounded-full border-[3px] border-[#1B1A2E] bg-white overflow-hidden">
            <div className="h-full bg-[#7B5CFA] transition-[width]" style={{ width: `${progress}%` }} />
          </div>
        )}
        <button data-testid="studio-submit-btn" disabled={!canSubmit} onClick={submit} className="zn-btn bg-[#FF6B4A] text-white w-full text-lg sticky bottom-[calc(5.75rem+env(safe-area-inset-bottom))] md:static z-10">
          <Send size={18} /> {progress !== null ? (progress < 100 ? `Uploading ${progress}%` : "AI safety check…") : "Send for approval"}
        </button>
        <p className="text-xs text-center text-[#1B1A2E]/60">AI checks your words and video, then your grown-up watches it first. Keep your house number, school and real name out of the shot!</p>
      </div>
    </div>
  );
}
